import React, { useEffect, useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Modal, Alert } from "react-native";
import Checkbox from "expo-checkbox";
import DatePicker from 'react-date-picker';
import { format, addDays, isAfter } from "date-fns";
import { useNav } from "../../../../providers/NavProvider";
import { useAuth } from "../../../../providers/AuthProvider";
import ToastMessage from "../controls/Toast";

type Props = {
    visible: boolean;
    onClose: () => void;
    apiEndpoint: string;
    logTitle: string;
    siteId: string;
};

const WeeklyReportModal: React.FC<Props> = ({ visible, onClose, apiEndpoint, logTitle, siteId }) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [showPicker, setShowPicker] = useState(false);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [includeGoodPractices, setIncludeGoodPractices] = useState(false); // default unchecked
    const [includeOpenPast, setIncludeOpenPast] = useState(true);            // default checked
    const [includeClosed, setIncludeClosed] = useState(true);
    const [projectId, setProjectId] = useState(null);
    const [packageTitle, setPackageTitle] = useState(null);
    const { sitesResp } = useNav();
    const { getAccessToken, userEmail } = useAuth();
    const [toast, setToast] = useState({
        show: false,
        message: "",
        actionLabel: "",
    });
    useEffect(() => {
        if (sitesResp) {

            const projectSite = findProjectParent(sitesResp, siteId);
            const packageSite = findImmediateParent(sitesResp, siteId);
            if (projectSite && projectSite.path.split('-').length === 2) {
                siteId && setProjectId(packageSite.id);
            }
            if (packageSite && packageSite.title) {
                siteId && setPackageTitle(packageSite.title.replace(/\s+/g, '_'));
            }
        }
    }, []);
    function findProjectParent(siteResp: any, siteId: string): any | null {
        function findParentById(site: any, id: string): any | null {
            if (!site.children) return null;

            for (const child of site.children) {
                if (child.id === id) return site; // Direct parent found
                const parent = findParentById(child, id);
                if (parent) return parent; // Recursively check parents
            }
            return null;
        }

        // Start from given site and move up until we reach project-level site
        let currentSite = siteResp;
        let parent = findParentById(siteResp, siteId);

        while (parent) {
            // If the parent is a project-level site (2 segments in path), return it
            if (parent.path.split("-").length === 2) {
                return parent;
            }
            // Move up the hierarchy
            currentSite = parent;
            parent = findParentById(siteResp, currentSite.id);
        }

        return null; // No project-level parent found
    }
    function findImmediateParent(siteResp: any, siteId: string): any | null {
        // Recursive helper to search through children
        function searchParent(site: any, id: string): any | null {
            if (!site.children) return null;

            for (const child of site.children) {
                if (child.id === id) {
                    return site; // Found direct parent
                }

                const parent = searchParent(child, id); // Recursively check deeper
                if (parent) {
                    return parent; // Propagate found parent up
                }
            }

            return null;
        }

        return searchParent(siteResp, siteId);
    }
    const handleSubmit = async () => {

        if (!startDate || !endDate) {
            //Alert.alert("Validation", "Please select a start date.");
            return;
        }
        const checks = {
            includeGoodPractices,
            includeOpenPast,
            includeClosed,
            packageTitle,
        };
        const reportPayload = {
            template: "WeeklyObservationsReportForContractor",
            period: `${startDate.toISOString().split("T")[0]} ${endDate.toISOString().split("T")[0]}`,
            logTitle: logTitle,
            AuthorId: userEmail,
            siteId: siteId,
            projectId: projectId,
            MoreDetails: JSON.stringify(checks),
        };

        try {
            const accessToken = await getAccessToken();
            const response = await triggerReportRequest(accessToken, apiEndpoint, reportPayload);
            if (!response.ok) {
                setToast({
                    show: true,
                    message: "Failed to place report request",
                    actionLabel: "Failed",
                });
            } else {
                setToast({
                    show: true,
                    message: "Report request placed successfully",
                    actionLabel: "OK",
                });
            }
            onClose();
        } catch (err) {
            //Alert.alert("Error", (err as Error).message);
        }
    };
    async function triggerReportRequest(
        accessToken: string,
        clientAPIURL: string,
        data: any
    ) {

        try {

            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(data),
            };
            const response = await fetch(`${clientAPIURL}api/triggerReportRequest`, requestOptions);
            console.log('Response: ', response);
            if (!response.ok) {
                // Handle HTTP errors
                console.error(`Error: ${response.status} ${response.statusText}`);
            }

            const result = response
            return result; // Return the parsed JSON response
        } catch (error) {
            console.error('Error in triggerReportRequest:', error);
            console.error(error) // Re-throw error for caller to handle if needed
        }
    }

    return (
        <Modal visible={visible} transparent animationType="slide">
            <ToastMessage
                show={toast.show}
                toastMessage={toast.message}
                duration={3000}
                actionLabel={toast.actionLabel}
                onDismiss={() => setToast({ ...toast, show: false })}
            />
            <View style={styles.overlay}>
                <View style={styles.container}>
                    <Text style={styles.title}>Select your reporting week</Text>

                    {/* Two column layout */}


                    {/* Checkboxes */}
                    <View style={styles.checkboxRow}>
                        <Checkbox
                            value={includeGoodPractices}
                            onValueChange={setIncludeGoodPractices}
                        //color={includeGoodPractices ? "#6200EE" : undefined}
                        />
                        <Text style={styles.checkboxLabel}>Include Good Practices</Text>
                    </View>

                    <View style={styles.checkboxRow}>
                        <Checkbox
                            value={includeOpenPast}
                            onValueChange={setIncludeOpenPast}
                        //color={includeGoodPractices ? "#6200EE" : undefined}
                        />
                        <Text style={styles.checkboxLabel}>
                            Include Observations raised in the past and still open
                        </Text>
                    </View>

                    <View style={styles.checkboxRow}>
                        <Checkbox
                            value={includeClosed}
                            onValueChange={setIncludeClosed}
                        //color={includeGoodPractices ? "#6200EE" : undefined}
                        />
                        <Text style={styles.checkboxLabel}>
                            Include Observations closed during this period
                        </Text>
                    </View>
                    <View style={styles.row}>
                        {/* Column 1: Start Date */}
                        <View style={styles.col}>
                            <Text style={styles.label}>Report Start Date</Text>
                            <DatePicker
                                className="custom-date-picker"
                                value={startDate}
                                onChange={(date: any) => { setStartDate(date); setEndDate(date ? addDays(date, 6) : null) }}
                                maxDate={new Date()} // only past dates allowed
                                autoFocus={false}
                                isOpen={false}
                            />
                        </View>

                        {/* Column 2: End Date */}
                        <View style={styles.col}>
                            <Text style={styles.label}>Report End Date</Text>
                            <DatePicker
                                className="custom-date-picker"
                                value={endDate}
                                onChange={(date: any) => setEndDate(date)}
                                minDate={startDate || undefined}
                                maxDate={new Date()}
                                autoFocus={false}
                                isOpen={false}
                            />
                        </View>
                    </View>
                    {/* Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity style={styles.cancelBtn} onPress={onClose}>
                            <Text style={styles.btnText}>Cancel</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.submitBtn} onPress={handleSubmit}>
                            <Text style={styles.btnText}>Submit</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

export default WeeklyReportModal;

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.5)",
        justifyContent: "center",
        alignItems: "center",
    },
    container: {
        width: "50%",
        backgroundColor: "#fff",
        padding: 20,
        borderRadius: 16,
        elevation: 4,
    },
    title: {
        fontSize: 18,
        fontWeight: "600",
        marginBottom: 20,
        textAlign: "center",
    },
    row: {
        flexDirection: "row",
        justifyContent: "space-between",
        zIndex: 9999,
    },
    col: {
        flex: 1,
        zIndex: 9999,
        marginHorizontal: 6,

    },
    label: {
        fontSize: 14,
        marginBottom: 6,
        fontWeight: "500",
    },
    buttonRow: {
        flexDirection: "row",
        justifyContent: "flex-end",
        marginTop: 20,
    },
    cancelBtn: {
        marginRight: 12,
        padding: 10,
        color: "#fff",
        backgroundColor: "#676767",
        borderRadius: 8,
    },
    checkboxRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 10,
    },
    checkboxLabel: {
        marginLeft: 8,
        fontSize: 14,
        flex: 1,
        flexWrap: "wrap",
    },
    submitBtn: {
        backgroundColor: "#4BB75F",
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
    },
    btnText: {
        color: "#fff",
        fontWeight: "600",
    },
});


